import React, { FC, useState } from 'react';
import { Link as BrowserLink, Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Amplify } from 'aws-amplify';
import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { TabContext, TabPanel } from '@material-ui/lab';
import { amplifyConfig, amplifyTechnicianConfig } from 'src/config';
import { InputField } from '../input-field';
import { useAuth } from '../../hooks/use-auth';
import { useMounted } from '../../hooks/use-mounted';

export const LoginAmplify: FC = () => {
  const [counter, setCounter] = React.useState(30);
  const mounted = useMounted();
  const navigate = useNavigate();
  const { login, getOtp, mobileNumberValid, resetOtpError, showVerifyOtp,
  } = useAuth() as any;
  const [tab, setTab] = useState<string>('admin');

  React.useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(Math.max(counter - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
      password: Yup.string().max(255).required('Password is required'),
      policy: Yup.boolean()
    }),
    onSubmit: async (values, helpers) => {
      try {
        await login(values.email, values.password);

        if (mounted.current) {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        }
      } catch (err) {
        console.error(err);

        if (err.code === 'UserNotConfirmedException') {
          navigate('/verify-code', { state: { username: values.email } });
          return;
        }

        if (mounted.current) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    }
  });

  const formikOtp = useFormik({
    initialValues: {
      mobileNumber: '',
      otp: '',
      submit: null
    },
    validationSchema: Yup.object().shape({
      mobileNumber: Yup.string().min(10).max(10).required('Mobile Number is required'),
      otp: Yup.string().min(6).max(6).required('OTP is required'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        await login(`+91${values.mobileNumber}`, values.otp);

        if (mounted.current) {
          helpers.setStatus({ success: true });
          helpers.setSubmitting(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: 'Invalid OTP, try again' });
          helpers.setSubmitting(false);
        }
      }
    }
  });
  const handleTabChange = (event, newValue) => {
    localStorage.setItem('tab', newValue);
    setTab(newValue);
  };
  const handleGetOtp = async () => {
    await getOtp(formikOtp.values.mobileNumber);
    setCounter(30);
  };

  React.useEffect(() => {
    if (tab === 'technician') {
      Amplify.configure(amplifyTechnicianConfig);
    } else {
      Amplify.configure(amplifyConfig);
    }
  }, [tab]);

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
      >
        {/* <Typography
          color="textPrimary"
          variant="h4"
        >
          Login
        </Typography> */}
        <Box sx={{ flexGrow: 1 }} />
      </Box>
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label="basic tabs example"
          >
            <Tab
              label="Admin / Partner"
              value="admin"
              style={{ width: '50%',
                margin: 0 }}
            />
            <Tab
              label="Technician"
              value="technician"
              style={{ width: '50%',
                margin: 0 }}
            />
          </Tabs>
        </Box>
        <TabPanel
          value="admin"
        >
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
              >
                <InputField
                  autoFocus
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  fullWidth
                  helperText={formik.touched.email && formik.errors.email}
                  label="Email address"
                  name="email"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="email"
                  value={formik.values.email}
                />
              </Grid>
              <Grid
                item
                xs={12}
              >
                <InputField
                  error={Boolean(formik.touched.password && formik.errors.password)}
                  fullWidth
                  helperText={formik.touched.password && formik.errors.password}
                  label="Password"
                  name="password"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  type="password"
                  value={formik.values.password}
                />
              </Grid>
              {formik.errors.submit && (
              <Grid
                item
                xs={12}
              >
                <FormHelperText error>
                  {formik.errors.submit}
                </FormHelperText>
              </Grid>
              )}
              <Grid
                item
                xs={12}
              >
                <Button
                  color="primary"
                  disabled={formik.isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Log In
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Button
                  color="primary"
                  component={BrowserLink}
                  to="/password-recovery"
                  variant="text"
                >
                  Forgot password
                </Button>
              </Grid>
            </Grid>
          </form>
        </TabPanel>
        <TabPanel
          value="technician"
        >
          <form
            onSubmit={formikOtp.handleSubmit}
          >
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
              >
                <InputField
                  autoFocus
                  error={Boolean(formikOtp.touched.mobileNumber && formikOtp.errors.mobileNumber)}
                  fullWidth
                  helperText={formikOtp.touched.mobileNumber && formikOtp.errors.mobileNumber}
                  label="Mobile Number"
                  name="mobileNumber"
                  onBlur={formikOtp.handleBlur}
                  onChange={(e) => { formikOtp.handleChange(e); resetOtpError(); }}
                  type="number"
                  value={formikOtp.values.mobileNumber}
                />
                {!mobileNumberValid && <Alert severity="error"> Mobile Number does not exist</Alert>}
              </Grid>
              <Grid
                item
                xs={12}
              >
                {!showVerifyOtp && (
                <Button
                  color="primary"
                  disabled={Boolean(formikOtp.touched.mobileNumber && formikOtp.errors.mobileNumber)
                     || !formikOtp.values.mobileNumber}
                  fullWidth
                  size="large"
                  type="button"
                  variant="contained"
                  onClick={handleGetOtp}
                >
                  Get OTP
                </Button>
                )}
              </Grid>
              {showVerifyOtp && (
              <>
                <Grid
                  item
                  xs={12}
                >
                  <InputField
                    autoFocus
                    error={Boolean(formikOtp.touched.otp && formikOtp.errors.otp)}
                    fullWidth
                    helperText={formikOtp.touched.otp && formikOtp.errors.otp}
                    label="Enter OTP"
                    name="otp"
                    onBlur={formikOtp.handleBlur}
                    onChange={(e) => { formikOtp.handleChange(e); }}
                    type="number"
                    value={formikOtp.values.otp}
                  />
                  <FormHelperText error>
                    {formikOtp.errors.submit}
                  </FormHelperText>
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <Button
                    color="primary"
                    disabled={formikOtp.isSubmitting || !(formikOtp.isValid && formikOtp.dirty)}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Verify OTP
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}
                >
                  {counter !== 0 ? (
                    <Typography
                      color="textPrimary"
                      variant="body2"
                    >
                      Resend OTP in
                      {' '}
                      <span style={{ fontWeight: 'bold' }}>
                        {counter}
                        s
                      </span>

                    </Typography>

                  )
                    : (
                      <Button
                        color="primary"
                        component={BrowserLink}
                        to=""
                        variant="text"
                        onClick={handleGetOtp}
                      >
                        Resend OTP
                      </Button>

                    )}
                </Grid>
              </>
              )}
            </Grid>
          </form>
        </TabPanel>
        <Typography
          textAlign="center"
          variant="body2"
        >
          {'By continuing you are agree to our '}
          <Link
            to="/privacy"
            target="_blank"
          >
            Privacy Policy
          </Link>
          {' and '}
          <Link
            to="/terms"
            target="_blank"
          >
            Terms and Conditions
          </Link>
        </Typography>
      </TabContext>
    </>
  );
};
